import * as React from 'react'

import {
  CircleAlertIcon,
  CircleCheckIcon,
  HourglassIcon,
  Link2OffIcon,
} from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { createPortal } from 'react-dom'
import { RouteComponentProps, withRouter } from 'react-router'

import {
  AuthLayout,
  AuthStatus,
  AuthStatusTone,
} from '~/client/src/shared/components/AuthLayout'
import { Button } from '~/client/src/shared/components/Button/Button'
import ContactHelp from '~/client/src/shared/components/ContactHelp/ContactHelp'
import ExpiredInviteKeyInfo from '~/client/src/shared/components/ExpiredInviteKeyInfo/ExpiredInviteKeyInfo'
import TraceIdDisplay from '~/client/src/shared/components/TraceIdDisplay'
import InfoCode from '~/client/src/shared/enums/InfoCode'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { GO_TO_AVAILABLE_PROJECT } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import InitialState from '~/client/src/shared/stores/InitialState'

import CommonStore from '../../stores/ui/Common.store'

const HELP_EMAIL = 'info@struxhub.com'

interface IStatus {
  icon: React.ReactNode
  tone?: AuthStatusTone
  title: string
  description: string
  content?: React.ReactNode
}

@inject('eventsStore', 'common', 'state')
@observer
class InfoView extends React.Component<
  RouteComponentProps<{ code: string; payload: string }> & {
    eventsStore: EventsStore
    common: CommonStore
    state: InitialState
  }
> {
  private readonly code: InfoCode = null
  private readonly payload: string = ''

  public constructor(props: any) {
    super(props)

    const { code, payload } = props.match.params
    this.code = code
    this.payload = payload
  }

  public render() {
    const { translator } = Localization

    // The mobile route slider's transform would otherwise contain the fixed
    // shell, so it would fill only the route area instead of the viewport.
    return createPortal(
      <AuthLayout showLanguageSelect>
        {this.body}
        <div className="tw:flex tw:flex-col tw:items-center tw:gap-2">
          <ContactHelp
            mailto={HELP_EMAIL}
            hint={translator.contactHelpAtMail(HELP_EMAIL)}
            subject={translator.contactHelp}
          />
        </div>
      </AuthLayout>,
      document.body,
    )
  }

  private get hasBackToLogin(): boolean {
    return (
      this.code !== InfoCode.ACCESS_TO_PROJECT_REQUESTED &&
      this.code !== InfoCode.EXPIRED_INVITE_TOKEN
    )
  }

  private get body(): JSX.Element {
    const t = Localization.translator.loginInfoPageDescriptions

    switch (this.code) {
      case InfoCode.EXPIRED_INVITE_TOKEN:
        return <ExpiredInviteKeyInfo expiredInviteKey={this.payload} />

      case InfoCode.INVALID_INVITE_TOKEN:
        return this.renderStatus({
          icon: <Link2OffIcon />,
          tone: 'destructive',
          title: t.invalidInvitationTitle,
          description: t.invalidInvitationDescription,
        })

      case InfoCode.ACCOUNT_CREATED:
        return this.renderStatus({
          icon: <HourglassIcon />,
          title: t.requestSentTitle,
          description:
            this.payload === 'true'
              ? t.accountRequestPendingEmail
              : t.accountRequestPendingSms,
        })

      case InfoCode.ACCOUNT_CREATED_AND_INVITED:
        return this.renderStatus({
          icon: <CircleCheckIcon />,
          tone: 'success',
          title: t.requestApprovedTitle,
          description: t.accountRequestApproved,
        })

      case InfoCode.ACCESS_TO_PROJECT_REQUESTED:
        return this.renderStatus({
          icon: <HourglassIcon />,
          title: t.requestSentTitle,
          description: t.accessRequestPending,
          content: (
            <Button className="tw:w-full" onClick={this.goToAvailableProject}>
              {t.goToAvailableProject}
            </Button>
          ),
        })

      default: {
        const traceId = this.props.state?.lastTraceId

        return this.renderStatus({
          icon: <CircleAlertIcon />,
          tone: 'destructive',
          title: t.somethingWentWrongTitle,
          description: t.somethingWentWrongDescription,
          content: traceId && <TraceIdDisplay traceId={traceId} />,
        })
      }
    }
  }

  private renderStatus({ content, ...status }: IStatus): JSX.Element {
    return (
      <AuthStatus {...status}>
        {content}
        {this.hasBackToLogin && (
          <Button className="tw:w-full" onClick={this.goToLogin}>
            {Localization.translator.backToLogin}
          </Button>
        )}
      </AuthStatus>
    )
  }

  private goToLogin = () => {
    this.props.common.displayLoginView()
  }

  private goToAvailableProject = () => {
    this.props.common.history.goBack()
    this.props.eventsStore.dispatch(GO_TO_AVAILABLE_PROJECT)
  }
}

export default withRouter(InfoView)
